import { useEffect } from "react";

import { switchOrg } from "api/api-server-actions";
import { MonitoringWebsocketProvider } from "providers/MonitoringWebsocketProvider";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import { selectCurrentCourse, selectOrgIdForCourseId } from "store/selectors";
import { selectCurrentUserId } from "store/selectors/base";
import { viewStateActions } from "store/slices/view";
import { getOrgIdFromCookie } from "utils/auth";
import { rollbarAndLogError, rollbarAndLogWarning } from "utils/logger";
import { Outlet, useParams } from "utils/routing";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

export default function Layout() {
  const dispatch = useAppDispatch();
  const course = useAppSelector(selectCurrentCourse);
  const courseVersion = course?.course_version;
  // Ensure this component refreshes when we navigate between the course routes
  const { courseId } = useParams();
  const orgIdForCourseId = useAppSelector((s) => selectOrgIdForCourseId(s, courseId));
  const orgIdFromCookie = getOrgIdFromCookie();
  const userId = useAppSelector(selectCurrentUserId);
  const needsOrgSwitch = orgIdForCourseId && orgIdForCourseId !== orgIdFromCookie;

  useEffect(() => {
    async function loadCourse() {
      if (!userId || !courseId) {
        return;
      }
      // If we detect that we need to switch org, we skip the course retrieval
      if (needsOrgSwitch) {
        return;
      }
      try {
        await storeApi.courses.retrieve(courseId, { skipToast: true });
        dispatch(viewStateActions.setCurrentCourseId(courseId));
      } catch (error) {
        if ([404, 403, 401].includes(error.status)) {
          rollbarAndLogWarning("Course not found, rerouting to index", error);
          window.location.href = "/";
        } else {
          rollbarAndLogError("Failed to load course", error);
        }
      }
    }
    loadCourse();
  }, [courseId, dispatch, userId, needsOrgSwitch]);

  useEffect(() => {
    if (courseVersion) {
      dispatch(viewStateActions.setCourseVersionTo(courseVersion));
    }
  }, [courseVersion, dispatch]);

  useEffect(() => {
    async function switchOrgIfNeeded() {
      if (needsOrgSwitch) {
        try {
          await switchOrg(orgIdForCourseId);
          window.location.reload();
        } catch (error) {
          // We don't show the error the user, because it's too technical. They will just see a 404.
          rollbarAndLogError("Failed to auto-switch org", error);
        }
      }
    }
    switchOrgIfNeeded();
  }, [needsOrgSwitch, orgIdForCourseId]);

  useEffect(() => {
    // Once we have the course and know it exists, we can start loading all related data
    if (course?.id && courseId) {
      storeApi.courses.list();
      storeApi.course_users.list({ course_id: courseId });
      storeApi.orgs.list();
      storeApi.class_sessions.list({ course_id: courseId });
      storeApi.class_users.list({ course_id: courseId });
      storeApi.recording_sessions.list({ course_id: courseId });
    }
  }, [course?.id, courseId]);

  return (
    <MonitoringWebsocketProvider>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </MonitoringWebsocketProvider>
  );
}
